import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, ChevronLeft, ChevronRight, Send, Landmark, HandCoins, ClipboardCheck, Receipt, ReceiptText, Upload as UploadIcon, ShoppingCart, MessagesSquare, BookOpen, ListChecks, MessageSquarePlus } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useTranslations } from 'use-intl';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/ui/avatar';
import ChatAssistant from '@/components/ai/ChatAssistant';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { NavigationItem } from '@/types/navigation';
import HarmonyLogo from '@/components/branding/HarmonyLogo';
import { isApprovalFlowEnabled } from '@/config/features';
import { FeedbackPanel } from '@/components/feedback/FeedbackPanel';

const getNavigationItems = (): NavigationItem[] => {
  const approvalEnabled = isApprovalFlowEnabled();

  const baseItems: NavigationItem[] = [
    {
      id: "overview",
      path: "/overview",
      icon: LayoutDashboard
    }, {
      id: "upload",
      path: "/upload",
      icon: UploadIcon
    }, {
      id: "review",
      path: "/review",
      icon: ClipboardCheck
    }, {
      id: "todos",
      path: "/todos",
      icon: ListChecks
    }, {
      id: "accountPayables",
      path: "/account-payables",
      icon: Send,
      subItems: [{
        id: "payments",
        path: "/account-payables/payments"
      }]
    }, {
      id: "accountReceivables",
      path: "/account-receivables",
      icon: HandCoins
    }, {
      id: "salesReports",
      path: "/sales-reports",
      icon: ShoppingCart
    }, {
      id: "expenses",
      path: "/expenses",
      icon: Receipt
    }, {
      id: "receipts",
      path: "/receipts",
      icon: ReceiptText
    }, {
      id: "journal",
      path: "/bookkeeping/journal",
      icon: BookOpen
    }, {
      id: "bank",
      path: "/bank",
      icon: Landmark
    }, {
      id: "messages",
      path: "/messages",
      icon: MessagesSquare
    }
  ];

  // Create Invoice (issue #287) is reachable from a button on the Account
  // Receivables page instead of its own nav entry — it isn't a distinct area of
  // the app, just an action within receivables.

  // Add Approvals submenu only if approval flow is enabled
  if (approvalEnabled) {
    const accountPayablesItem = baseItems.find(item => item.id === "accountPayables");
    if (accountPayablesItem && accountPayablesItem.subItems) {
      accountPayablesItem.subItems.push({
        id: "approvals",
        path: "/account-payables/approvals"
      });
    }
  }

  return baseItems;
};

const Sidebar = () => {
  const t = useTranslations('consultantShell');
  const [collapsed, setCollapsed] = useState(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState ? savedState === 'true' : false;
  });
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [showAssistant, setShowAssistant] = useState(false);
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get navigation items dynamically based on feature flags
  const navigationItems = useMemo(() => getNavigationItems(), []);

  // Check if current path is within a specific section (for highlighting the parent menu item)
  const isInSection = (path: string) => location.pathname.startsWith(path);

  // Save collapsed state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
    // Dispatch an event to notify other components about the change
    window.dispatchEvent(new Event('storage'));
    
    // Hide assistant when sidebar is expanded
    if (!collapsed) {
      setShowAssistant(false);
    }
  }, [collapsed]);

  // Determine if a menu item should be expanded based on the current route
  useEffect(() => {
    const newExpandedItems: string[] = [];
    navigationItems.forEach(item => {
      if (item.subItems && item.subItems.some(subItem => location.pathname === subItem.path)) {
        newExpandedItems.push(item.id);
      }
    });

    setExpandedItems(newExpandedItems);
  }, [location.pathname, navigationItems]);

  const toggleExpand = (itemId: string) => {
    setExpandedItems(prev => prev.includes(itemId) ? prev.filter(item => item !== itemId) : [...prev, itemId]);
  };

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
    setShowAssistant(false);
  };

  const toggleAssistant = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAssistant(!showAssistant);
  };

  const handleAssistantMinimize = () => {
    setShowAssistant(false);
  };

  const handleSubItemClick = (subItemPath: string) => {
    // Close popover and navigate
    setOpenPopover(null);
    navigate(subItemPath);
  };

  return (
    <div className={cn(
      "h-screen flex-shrink-0 bg-background border-r transition-all duration-300 ease-in-out fixed left-0 z-50 flex flex-col", 
      collapsed ? "w-16" : "w-64"
    )}>
      <div className="flex items-center h-16 px-4 border-b relative z-60">
        {!collapsed && (
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <HarmonyLogo size="md" showText={true} />
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center w-full cursor-pointer" onClick={() => navigate('/')}>
            <HarmonyLogo size="md" showText={false} />
          </div>
        )}
          
        <button onClick={toggleCollapse} className="absolute right-4 flex h-6 w-6 items-center justify-center rounded-full border bg-background shadow-subtle">
          {collapsed ? <ChevronRight size={14} className="text-primary" /> : <ChevronLeft size={14} className="text-primary" />}
        </button>
      </div>
      
      {/* Navigation Menu */}
      <nav className="p-2 overflow-y-auto flex-grow" style={{ maxHeight: 'calc(100vh - 180px)' }}>
        <ul className="space-y-1">
          {navigationItems.map(item => <li key={item.id} className="flex flex-col">
              {item.subItems ? (
                collapsed ? (
                  <TooltipProvider>
                    <Popover open={openPopover === item.id} onOpenChange={(open) => setOpenPopover(open ? item.id : null)}>
                      <PopoverTrigger asChild>
                        <button 
                          className={cn(
                            "sidebar-link w-full flex items-center justify-center px-2",
                            isInSection(item.path) && "sidebar-link-active"
                          )}
                        >
                          <item.icon size={20} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent side="right" align="start" className="p-0 w-48">
                        <div className="py-1">
                          {item.subItems.map(subItem => (
                            <button
                              key={subItem.id}
                              className={cn(
                                "block w-full text-left px-4 py-2 text-sm hover:bg-accent",
                                location.pathname === subItem.path && "bg-accent text-accent-foreground font-medium"
                              )}
                              onClick={() => handleSubItemClick(subItem.path)}
                            >
                              {t(`nav.${subItem.id}`)}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </TooltipProvider>
                ) : (
                  <>
                    <button 
                      onClick={() => toggleExpand(item.id)} 
                      className={cn("sidebar-link w-full flex items-center",
                        isInSection(item.path) && "sidebar-link-active")}
                    >
                      <item.icon size={20} />
                      <span className="flex-1 text-left">{t(`nav.${item.id}`)}</span>
                    </button>
                    
                    {!collapsed && expandedItems.includes(item.id) && <ul className="pl-6 mt-1 space-y-1">
                        {item.subItems.map(subItem => <li key={subItem.id}>
                            <NavLink to={subItem.path} className={({
                      isActive
                    }) => cn("sidebar-link text-sm py-1.5", isActive && "sidebar-link-active")}>
                              <span>{t(`nav.${subItem.id}`)}</span>
                            </NavLink>
                          </li>)}
                      </ul>}
                  </>
                )
              ) : (
                <NavLink 
                  to={item.path} 
                  className={({isActive}) => cn(
                    "sidebar-link", 
                    isActive && "sidebar-link-active", 
                    collapsed && "justify-center px-2"
                  )} 
                  end
                >
                  <item.icon size={20} />
                  {!collapsed && <span>{t(`nav.${item.id}`)}</span>}
                </NavLink>
              )}
            </li>)}
        </ul>
      </nav>

      {/* Feedback entry (in-app-feedback F2/AC1) — footer slot above the account area (Draft A,
          screens.md S3): visible on every page, never covers content. */}
      <div className="p-2 border-t mt-auto">
        {collapsed ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="sidebar-link w-full flex items-center justify-center px-2"
                  onClick={() => setFeedbackOpen(true)}
                  aria-label={t('nav.feedback')}
                >
                  <MessageSquarePlus size={20} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{t('nav.feedback')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <button
            className="sidebar-link w-full flex items-center"
            onClick={() => setFeedbackOpen(true)}
          >
            <MessageSquarePlus size={20} />
            <span>{t('nav.feedback')}</span>
          </button>
        )}
      </div>
      <FeedbackPanel open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
};

export default Sidebar;
